import Cookie from 'js-cookie'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { ChordAnalytics, type ObjectTypes } from '@chordcommerce/analytics'

import { updateCartAttributesReconcile } from '@/clients/shopify/client'
import {
  cartFormatter,
  checkoutFormatter,
  lineItemFormatter,
  productFormatter,
} from '@/config/chord-analytics-formatters'
import type { Cart } from '@/types/shopify'
import { captureException } from '@/utilities/logger-fe'

const segment_key = import.meta.env.PUBLIC_SEGMENT_ANALYTICS_KEY

const analytics = new AnalyticsBrowser()

if (typeof window !== 'undefined') {
  analytics.addSourceMiddleware(({ payload, next }) => {
    let consents = {}
    const ketchToken = Cookie.get('_ketch_consent_v1_')
    if (ketchToken) {
      const decodedConsents = JSON.parse(atob(ketchToken))

      consents = Object.keys(decodedConsents).reduce(
        (acc, key) => {
          acc[key] = decodedConsents[key].status === 'granted'
          return acc
        },
        {} as Record<string, boolean>,
      )
    }

    payload.obj.context = {
      ...payload.obj.context,
      consent: {
        categoryPreference: { ...consents },
      },
    }

    next(payload)
  })

  analytics.load({ writeKey: segment_key })
}

export const chord =
  typeof window !== 'undefined'
    ? new ChordAnalytics<ObjectTypes>({
        cdp: () => analytics,
        cdpDomain: import.meta.env.PUBLIC_CHORD_CDP_DOMAIN,
        cdpWriteKey: import.meta.env.PUBLIC_CHORD_CDP_WRITE_KEY,
        consent: 'ketch',
        formatters: {
          objects: {
            cart: cartFormatter,
            checkout: checkoutFormatter,
            lineItem: lineItemFormatter,
            product: productFormatter,
          },
        },
        metadata: {
          i18n: {
            currency: 'USD',
            locale: 'en-us',
          },
          ownership: {
            omsId: import.meta.env.PUBLIC_OMS_ID,
            storeId: import.meta.env.PUBLIC_STORE_ID,
            tenantId: import.meta.env.PUBLIC_TENANT_ID,
          },
          platform: {
            name: 'Shopify',
            type: 'web',
          },
          store: {
            domain: import.meta.env.PUBLIC_SHOPIFY_STORE_NAME,
          },
        },
      })
    : null

export const saveSegmentUserIdToCart = async (cart: Cart) => {
  if (analytics) {
    try {
      const analyticsUser = await analytics.user()
      const analyticsUserId = analyticsUser.anonymousId()
      const isSegmentIdPresent = (cart.attributes || []).some(
        (attribute) => attribute.key === 'segmentId' && !!attribute.value,
      )

      if (!analyticsUserId || isSegmentIdPresent) {
        console.warn(
          'Segment analytics user is either not available or was already added to cart attributes',
        )
      } else {
        await updateCartAttributesReconcile(cart.id, [
          ...cart.attributes,
          { key: 'segmentId', value: analyticsUserId },
        ])
      }
    } catch (error) {
      captureException(error)
    }
  }
}

export const saveChordUserIdToCart = async (cart: Cart) => {
  if (window._chord) {
    try {
      const analyticsUserId = window._chord.user().anonymousId
      const isChordIdPresent = (cart.attributes || []).some(
        (attribute) => attribute.key === '_cdpAnonymousId' && !!attribute.value,
      )

      if (!analyticsUserId || isChordIdPresent) {
        console.warn(
          'Chord CDP user is either not available or was already added to cart attributes',
        )
      } else {
        await updateCartAttributesReconcile(cart.id, [
          ...cart.attributes,
          { key: '_cdpAnonymousId', value: analyticsUserId },
        ])
      }
    } catch (error) {
      captureException(error)
    }
  }
}
